import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Loader2, ExternalLink, FileCheck, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const DOC_LABELS = { pan: 'PAN Card', aadhaar: 'Aadhaar Card', signatory: 'Signatory KYC' };

const KYCReviewModal = ({ open, onClose, sub, api, onReviewed }) => {
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(null);

  if (!sub) return null;
  const docs = sub.kyc_documents || {};
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  const downloadDoc = async (docType) => {
    try {
      const res = await fetch(`${API_URL}/api/virtual-office/subscriptions/${sub.id}/kyc/${docType}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) {
      toast.error('Failed to open document');
    }
  };

  const submitReview = async (decision) => {
    if (decision === 'rejected' && comment.trim().length < 3) {
      toast.error('Add a comment explaining what to fix');
      return;
    }
    setSubmitting(decision);
    try {
      await api.patch(`/virtual-office/subscriptions/${sub.id}/kyc/review`, { decision, comment });
      toast.success(`KYC ${decision}`);
      onReviewed && onReviewed();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Review failed');
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="kyc-review-modal">
        <DialogHeader>
          <DialogTitle>Review KYC — {sub.company_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Card className="bg-muted/30">
            <CardContent className="p-3 text-sm space-y-0.5">
              <p><strong>Plan:</strong> {sub.tier_name}</p>
              <p><strong>Contact:</strong> {sub.contact_name} · {sub.email}</p>
              {sub.gstin && <p><strong>GSTIN:</strong> <span className="font-mono">{sub.gstin}</span></p>}
              <p><strong>Submitted:</strong> {sub.kyc_submitted_at ? new Date(sub.kyc_submitted_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</p>
            </CardContent>
          </Card>

          <div className="space-y-2">
            {['pan', 'aadhaar', 'signatory'].map((dt) => {
              const doc = docs[dt];
              return (
                <div key={dt} className="flex items-center justify-between p-2.5 rounded border" data-testid={`kyc-review-doc-${dt}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{DOC_LABELS[dt]}</p>
                    {doc ? (
                      <p className="text-xs text-muted-foreground truncate">{doc.filename}</p>
                    ) : (
                      <p className="text-xs text-red-500">Not uploaded</p>
                    )}
                  </div>
                  {doc && (
                    <Button size="sm" variant="outline" onClick={() => downloadDoc(dt)} data-testid={`kyc-review-view-${dt}`}>
                      <ExternalLink className="w-3 h-3 mr-1" /> View
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          <div>
            <Label>Comment (required if rejecting)</Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="e.g. PAN image is blurry, please re-upload"
              data-testid="kyc-review-comment"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button variant="destructive" onClick={() => submitReview('rejected')} disabled={!!submitting} data-testid="kyc-review-reject">
            {submitting === 'rejected' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
            Reject
          </Button>
          <Button onClick={() => submitReview('approved')} disabled={!!submitting} data-testid="kyc-review-approve">
            {submitting === 'approved' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KYCReviewModal;
